import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/auth/Login";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => {
        setInvite(data);
        const parts = (data.name || "").trim().split(/\s+/);
        setForm((f) => ({ ...f, first_name: parts[0] || "", last_name: parts.slice(1).join(" ") || "" }));
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Invalid invite link"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/signup-admin", { token, ...form });
      toast.success("Account created! You can sign in now.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <AuthShell title="Invite unavailable" subtitle="This invitation can't be used.">
        <p className="text-sm text-slate-500" data-testid="invite-error">{error}</p>
        <p className="mt-6 text-center text-sm text-slate-500">
          <Link to="/login" className="font-semibold text-sky-600 hover:underline">Back to sign in</Link>
        </p>
      </AuthShell>
    );
  }
  if (!invite) {
    return <AuthShell title="Loading invite…" subtitle=""><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></AuthShell>;
  }

  return (
    <AuthShell title={`Join as ${invite.role_label}`} subtitle={`${invite.invited_by_name || "A coach"} invited you${invite.team_name ? ` (overseeing ${invite.team_name})` : ""}.`}>
      <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="invite-instant-note">
          <ShieldCheck className="h-4 w-4" /> Accounts created from this link have instant access.
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input value={invite.email} disabled data-testid="invite-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>First name</Label>
            <Input required value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="invite-firstname" />
          </div>
          <div className="space-y-2">
            <Label>Last name</Label>
            <Input required value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="invite-lastname" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Password</Label>
          <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="invite-password" />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="invite-submit">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
        </Button>
      </form>
    </AuthShell>
  );
}
